import {t, Trans} from '@lingui/macro';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {CheckBox} from '../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {PasswordBox} from '../../../libs/shared/ui/src/components/boxedInputs/passwordBox';
import {TextBox} from '../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {Dialog} from '../../components/common/dialogs/dialog';
import {DialogHeader} from '../../components/common/dialogs/dialogHeader';
import {GridAreaItem} from '../../components/common/grid';
import {Paragraph} from '../../components/common/paragraph';
import {useAppDispatch} from '../../helpers/reduxHelpers';
import {ApiTeammate, TeammateModel} from '../../models/api/teammateModel';
import {postApiAsync, putApiAsync, putAsync} from '../../services/http/http';
import {pushTeammateUpdated} from '../../store/data/teammates/teammatesActions';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {SettingsFieldLine} from '../common/settingsFieldLine';

/*
 * Props.
 */

interface SettingsProfile2FaProps {
  user: TeammateModel;
}

/*
 * Component.
 */

enum SettingsProfile2FaDialogStatesEnum {
  HIDDEN = 'HIDDEN',
  DISABLE = 'DISABLE',
  ENABLE = 'ENABLE',
}

export const SettingsProfile2FA: FC<SettingsProfile2FaProps> = (props) => {
  const {user} = props;
  const [dialogState, setDialogState] = useState<SettingsProfile2FaDialogStatesEnum>(
    SettingsProfile2FaDialogStatesEnum.HIDDEN,
  );
  const {showBanner} = useSettingsBanners();

  const maybeRenderDialog = () => {
    if (dialogState === SettingsProfile2FaDialogStatesEnum.HIDDEN) {
      return null;
    }

    return (
      <SettingsProfile2FaDialog
        onRequestClose={(isEnabling) => {
          setDialogState(SettingsProfile2FaDialogStatesEnum.HIDDEN);
          if (isEnabling === true) {
            showBanner({
              type: SettingsBannersEnum.SUCCESS,
              message: (
                <Trans id="settingsprofile-2faenabled">Two-factor authentication has been enabled</Trans>
              ),
            });
          } else if (isEnabling === false) {
            showBanner({
              type: SettingsBannersEnum.SUCCESS,
              message: (
                <Trans id="settingsprofile-2fadisabled">Two-factor authentication has been disabled</Trans>
              ),
            });
          }
        }}
        isDisabling={dialogState === SettingsProfile2FaDialogStatesEnum.DISABLE}
        user={user}
      />
    );
  };

  return (
    <>
      {maybeRenderDialog()}
      <SettingsFieldLine
        title={<Trans id="settingsprofile-2fatitle">Two-factor authentication</Trans>}
        subtitle={
          <Trans id="settingsprofile-2fasubtitle">
            Add an extra layer of security to your account by requiring access to your phone when you log in
          </Trans>
        }
        isSubtitleMultiline
        content={
          <Button
            variant={user.is2FAEnabled ? ButtonVariantsEnum.SECONDARY_DANGER : ButtonVariantsEnum.SECONDARY}
            size={VisualSizesEnum.MEDIUM}
            onClick={() => {
              if (user.is2FAEnabled) {
                setDialogState(SettingsProfile2FaDialogStatesEnum.DISABLE);
              } else {
                setDialogState(SettingsProfile2FaDialogStatesEnum.ENABLE);
              }
            }}
          >
            {user.is2FAEnabled ? (
              <Trans id="settingsprofile-disable2fa">Disable authentication</Trans>
            ) : (
              <Trans id="settingsprofile-enable2fa">Enable authentication</Trans>
            )}
          </Button>
        }
      />
    </>
  );
};

interface SettingsProfile2FaDialogProps {
  isDisabling: boolean;
  onRequestClose: (isEnabling: boolean | null) => void;
  user: TeammateModel;
}
enum SettingsProfile2FaDialogStepsEnum {
  PASSWORD = 'PASSWORD',
  TOKEN = 'TOKEN',
}
const StyledWrapperDiv = styled.div<{$bigGaps: boolean}>`
  margin: 20px;
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$bigGaps ? '10px' : '2px')};
`;
const StyledButtonGroupDiv = styled.div`
  padding-top: 7px;
  padding: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const StyledStepDiv = styled.div`
  display: grid;
  grid-template-areas:
    'step . title'
    'copy copy copy';
  grid-row-gap: 10px;
  grid-template-columns: auto 8px 1fr;
  grid-auto-rows: auto;
  padding-bottom: 20px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  padding-left: 20px;
  padding-right: 20px;
  margin-left: -20px;
  margin-right: -20px;

  & + & {
    padding-top: 20px;
  }

  &:last-child {
    padding-bottom: 8px;
    margin-bottom: -13px;
  }
`;
const StyledNumberBadge = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  ${(p) => boxShadow(p.theme, `0 0 0 1.5px ${p.theme.greys.black}`)};
  font-size: 9px;
  font-weight: ${fontWeights.black};
  line-height: 16px;
  text-align: center;
`;
const StyledStepTitle = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.large};
`;
const StyledTokenTextBox = styled(TextBox)`
  width: 100px;
`;
const StyledLink = styled.a`
  color: ${(p) => p.theme.palette.blue.shade40};
  display: inline-block;
  &:hover {
    color: ${(p) => p.theme.palette.blue.shade50};
  }
`;

const StyledParagraph = styled(Paragraph)`
  color: ${(p) => p.theme.greys.shade70};
`;

enum Profile2FaErrorsEnum {
  INVALID_PASSWORD = 'INVALID_PASSWORD',
  INVALID_TOKEN = 'INVALID_TOKEN',
}

const SettingsProfile2FaDialog: FC<SettingsProfile2FaDialogProps> = (props) => {
  const [step, setStep] = useState<SettingsProfile2FaDialogStepsEnum>(
    SettingsProfile2FaDialogStepsEnum.PASSWORD,
  );
  const {isDisabling, onRequestClose, user} = props;
  const [showPassword, setShowPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [errors, setErrors] = useState<Profile2FaErrorsEnum[]>([]);
  const [token, setToken] = useState('');

  const {showBanner} = useSettingsBanners();
  const [qrcodeURL, setQrcodeURL] = useState('');
  const dispatch = useAppDispatch();

  const canSave = useMemo(() => errors.length === 0, [errors.length]);
  const PasswordField = showPassword ? TextBox : PasswordBox;

  const handle2FaError = (error: any) => {
    if (error.reason === 'bad_password') {
      setErrors([Profile2FaErrorsEnum.INVALID_PASSWORD]);
    } else if (error.reason === 'invalid_token') {
      setErrors([Profile2FaErrorsEnum.INVALID_TOKEN]);
    } else {
      reportToBugsnag(error);
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: error.reason || error.message || (
          <Trans id="settingsprofile-genericerror">An error occurred</Trans>
        ),
      });
    }
  };
  const localize = useLocalize();

  const disable2Fa = async () => {
    setErrors([]);
    try {
      const updatedUser = await putApiAsync(ApiTeammate, `${user.url}/two_factor_auth`, {
        action: 'disable',
        password: currentPassword,
      });
      dispatch(pushTeammateUpdated(updatedUser));
      onRequestClose(false);
    } catch (error) {
      handle2FaError(error);
    }
  };

  const setup2Fa = async () => {
    setErrors([]);
    try {
      const response = await putAsync(`${user.url}/two_factor_auth`, {
        action: 'enable',
        password: currentPassword,
      });
      setQrcodeURL(response.two_factor_auth_qrcode_url || '');
      setStep(SettingsProfile2FaDialogStepsEnum.TOKEN);
    } catch (error) {
      handle2FaError(error);
    }
  };

  const finish2Fa = async () => {
    setErrors([]);
    try {
      const updatedUser = await postApiAsync(ApiTeammate, `${user.url}/two_factor_auth`, {
        password: currentPassword,
        token,
      });
      dispatch(pushTeammateUpdated(updatedUser));
      onRequestClose(true);
    } catch (error) {
      handle2FaError(error);
    }
  };

  if (step === SettingsProfile2FaDialogStepsEnum.PASSWORD) {
    return (
      <Dialog
        onRequestClose={() => {
          onRequestClose(null);
        }}
      >
        <DialogHeader
          title={
            isDisabling ? (
              <Trans id="settingsprofile-disabletwofa">Disable two-factor authentication</Trans>
            ) : (
              <Trans id="settingsprofile-enabletwofa">Enable two-factor authentication</Trans>
            )
          }
        />
        <StyledWrapperDiv $bigGaps>
          <Paragraph>
            <Trans id="settingsprofile-2facopy">
              For security, please enter your password for <br /> {user.email}.
            </Trans>
          </Paragraph>
          <div>
            <div>
              <PasswordField
                defaultValue={currentPassword}
                label={<Trans id="settingsprofile-2facurrentpassword">Current password</Trans>}
                onChange={(v) => {
                  setErrors([]);
                  setCurrentPassword(v);
                }}
                hasError={errors.includes(Profile2FaErrorsEnum.INVALID_PASSWORD)}
                errorMessage={<Trans id="settingsprofile-2faincorrectpassword">Incorrect password</Trans>}
                placeholder={localize(t('settingsprofile2fa-password')`Enter current password`)}
              />
            </div>
            <div>
              <CheckBox
                onChange={() => {
                  setShowPassword((p) => !p);
                }}
                isChecked={showPassword}
              >
                <Trans id="settingsprofile-showpassword">Show password</Trans>
              </CheckBox>
            </div>
          </div>
        </StyledWrapperDiv>
        <StyledButtonGroupDiv>
          <Button
            variant={ButtonVariantsEnum.SECONDARY}
            onClick={() => {
              onRequestClose(null);
            }}
          >
            <Trans id="settingsprofile-cancel">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            isDisabled={!canSave}
            onClick={() => {
              if (isDisabling) {
                disable2Fa();
              } else {
                setup2Fa();
              }
            }}
          >
            {isDisabling ? (
              <Trans id="settingsprofile-save">Save</Trans>
            ) : (
              <Trans id="settingsprofile-next">Next</Trans>
            )}
          </Button>
        </StyledButtonGroupDiv>
      </Dialog>
    );
  }

  return (
    <Dialog
      onRequestClose={() => {
        onRequestClose(null);
      }}
      width={600}
    >
      <DialogHeader
        title={<Trans id="settingsprofile-enabletwofa">Enable two-factor authentication</Trans>}
      />
      <StyledWrapperDiv $bigGaps>
        <StyledStepDiv>
          <GridAreaItem area="step">
            <StyledNumberBadge>1</StyledNumberBadge>
          </GridAreaItem>
          <GridAreaItem area="title">
            <StyledStepTitle>
              <Trans id="settingsprofile-download2fa">Download an authenticator app</Trans>
            </StyledStepTitle>
          </GridAreaItem>

          <GridAreaItem area="copy">
            <StyledParagraph>
              <Trans id="settingsprofile-download2fa-copy">
                Download and install{' '}
                <StyledLink target="_blank" rel="noopener noreferrer" href="https://authy.com">
                  Authy
                </StyledLink>{' '}
                or{' '}
                <StyledLink
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://support.google.com/accounts/answer/1066447?hl=en"
                >
                  Google Authenticator
                </StyledLink>{' '}
                for your phone.
              </Trans>
            </StyledParagraph>
          </GridAreaItem>
        </StyledStepDiv>
        <StyledStepDiv>
          <GridAreaItem area="step">
            <StyledNumberBadge>2</StyledNumberBadge>
          </GridAreaItem>
          <GridAreaItem area="title">
            <StyledStepTitle>
              <Trans id="settingsprofile-2fascanqrcode">Scan the QR code</Trans>
            </StyledStepTitle>
          </GridAreaItem>

          <GridAreaItem area="copy">
            <StyledParagraph>
              <Trans id="settingsprofile-2faopenapp">
                Open the authentication app and scan the QR code using your phone&apos;s camera.
              </Trans>
            </StyledParagraph>
            <img src={qrcodeURL} width={150} height={150} alt="" />
          </GridAreaItem>
        </StyledStepDiv>
        <StyledStepDiv>
          <GridAreaItem area="step">
            <StyledNumberBadge>3</StyledNumberBadge>
          </GridAreaItem>
          <GridAreaItem area="title">
            <StyledStepTitle>
              <Trans id="settingsprofile-2faloginwithcode">Login with your code</Trans>
            </StyledStepTitle>
          </GridAreaItem>

          <GridAreaItem area="copy">
            <StyledParagraph>
              <Trans id="settingsprofile-2faentercode">Enter the 6-digit code.</Trans>
            </StyledParagraph>
            <StyledTokenTextBox
              placeholder="000 000"
              onChange={(value) => {
                setToken(value.replace(/[^\d]/g, '').slice(0, 6));
              }}
              hasError={errors.includes(Profile2FaErrorsEnum.INVALID_TOKEN)}
              errorMessage={<Trans id="settingsprofile-2fainvalidcode">Code does not match</Trans>}
            />
          </GridAreaItem>
        </StyledStepDiv>
      </StyledWrapperDiv>
      <StyledButtonGroupDiv>
        <Button
          variant={ButtonVariantsEnum.SECONDARY}
          onClick={() => {
            onRequestClose(null);
          }}
        >
          <Trans id="settingsprofile-cancel">Cancel</Trans>
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} isDisabled={!canSave} onClick={finish2Fa}>
          <Trans id="settingsprofile-activate">Activate</Trans>
        </Button>
      </StyledButtonGroupDiv>
    </Dialog>
  );
};
